import { useState, useCallback, useRef, useEffect } from 'react';
import { useChatOpen, useSharedChat, useIsOnAIPage } from '../../hooks/useAgentContext';
import { getConversation } from '../../services/agentApi';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ConversationHistory from './ConversationHistory';

const MIN_WIDTH = 320;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 380;

export function ChatToggleButton() {
    const { isChatOpen, setIsChatOpen } = useChatOpen();
    const isOnAIPage = useIsOnAIPage();

    if (isChatOpen || isOnAIPage) return null;

    return (
        <button
            onClick={() => setIsChatOpen(true)}
            className="fixed bottom-6 right-6 z-50 p-3 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-all hover:scale-105"
            title="Open Prisme AI"
        >
            <span className="material-symbols-outlined text-[24px]">chat</span>
        </button>
    );
}

export default function ChatPanel() {
    const { setIsChatOpen } = useChatOpen();
    const { messages, isLoading, isThinking, isAvailable, conversationId, autoConfirm, setAutoConfirm, sendMessage, handleConfirmation, stopGeneration, clearChat, loadConversation } = useSharedChat();

    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const [showHistory, setShowHistory] = useState(false);
    const isResizing = useRef(false);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isResizing.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, []);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing.current) return;
            const newWidth = window.innerWidth - e.clientX;
            setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, newWidth)));
        };

        const handleMouseUp = () => {
            if (!isResizing.current) return;
            isResizing.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    async function handleSelectConversation(selectedId: string) {
        try {
            const detail = await getConversation(selectedId);
            loadConversation(detail.conversation_id, detail.messages);
            setShowHistory(false);
        } catch (err) {
            console.error('Failed to load conversation:', err);
        }
    }

    function handleNewChat() {
        clearChat();
        setShowHistory(false);
    }

    return (
        <div className="shrink-0 flex flex-col bg-white border-l border-slate-200 relative" style={{ width }}>
            {/* Resize handle */}
            <div
                onMouseDown={handleMouseDown}
                className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/30 active:bg-primary/50 z-10"
            />

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white shrink-0">
                <div className="flex items-center gap-2">
                    {isAvailable !== null && (
                        <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${isAvailable ? (isLoading ? 'bg-green-500 animate-pulse-strong' : 'bg-green-500') : 'bg-slate-400'}`} />
                    )}
                    <h3 className="text-sm font-semibold text-slate-800">
                        {showHistory ? 'Conversations' : 'Prisme AI'}
                    </h3>
                    {!showHistory && (
                        <button
                            onClick={() => setAutoConfirm(!autoConfirm)}
                            className={`text-xs px-1.5 py-0.5 rounded border transition-colors cursor-pointer ${
                                autoConfirm
                                    ? 'text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100 hover:border-amber-300'
                                    : 'text-slate-400 bg-slate-50 border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                            }`}
                            title={autoConfirm ? 'Click to disable auto-confirm' : 'Click to enable auto-confirm'}
                        >
                            {autoConfirm ? 'Auto-confirm on' : 'Auto-confirm off'}
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {/* New chat button */}
                    <button
                        onClick={handleNewChat}
                        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                        title="New chat"
                    >
                        <span className="material-symbols-outlined text-[16px]">add</span>
                    </button>
                    {/* History toggle */}
                    <button
                        onClick={() => setShowHistory(!showHistory)}
                        className={`p-1.5 rounded-md transition-colors ${
                            showHistory
                                ? 'text-primary bg-primary/10'
                                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                        }`}
                        title={showHistory ? 'Back to chat' : 'Conversation history'}
                    >
                        <span className="material-symbols-outlined text-[16px]">history</span>
                    </button>
                    {/* Close button */}
                    <button
                        onClick={() => setIsChatOpen(false)}
                        className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                        title="Close"
                    >
                        <span className="material-symbols-outlined text-[16px]">close</span>
                    </button>
                </div>
            </div>

            {showHistory ? (
                /* Conversation history */
                <ConversationHistory
                    onSelect={handleSelectConversation}
                    onClose={() => setShowHistory(false)}
                    currentConversationId={conversationId}
                />
            ) : (
                <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                    {/* Top spacer — centers content when empty, collapses when messages */}
                    <div className={`basis-0 shrink-0 transition-[flex-grow] duration-700 ease-out ${
                        messages.length === 0 ? 'grow' : 'grow-0'
                    }`} />

                    {/* Welcome text — fades & collapses */}
                    <div className={`shrink-0 text-center px-6 transition-all duration-500 ease-out overflow-hidden ${
                        messages.length === 0
                            ? 'opacity-100 max-h-32 mb-4'
                            : 'opacity-0 max-h-0 mb-0'
                    }`}>
                        <div className="text-slate-300 mb-3">
                            <span className="material-symbols-outlined text-[48px] mx-auto block w-fit">chat</span>
                        </div>
                        <p className="text-sm font-medium text-slate-600">How can I help you?</p>
                        {/* <p className="text-xs text-slate-400 mt-1">Ask me about your financial model</p> */}
                    </div>

                    {/* Messages area — grows when messages arrive */}
                    <div className={`min-h-0 flex flex-col overflow-hidden transition-[flex-grow] duration-700 ease-out ${
                        messages.length > 0 ? 'grow' : 'grow-0'
                    }`}>
                        {messages.length > 0 && (
                            <MessageList
                                messages={messages}
                                isLoading={isLoading}
                                isThinking={isThinking}
                                onConfirm={(toolCallId) => handleConfirmation(toolCallId, true)}
                                onConfirmAll={(toolCallId) => {
                                    setAutoConfirm(true);
                                    handleConfirmation(toolCallId, true);
                                }}
                                onCancel={(toolCallId) => handleConfirmation(toolCallId, false)}
                            />
                        )}
                    </div>

                    {/* Input — always rendered, same style */}
                    <MessageInput
                        onSend={sendMessage}
                        disabled={isLoading}
                        isLoading={isLoading}
                        onStop={stopGeneration}
                    />

                    {/* Bottom spacer — larger than top to push content upward */}
                    <div className={`basis-0 shrink-0 transition-[flex-grow] duration-700 ease-out ${
                        messages.length === 0 ? 'grow-[2]' : 'grow-0'
                    }`} />
                </div>
            )}
        </div>
    );
}
